import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormGetValues,
  FieldValues,
  Path,
} from 'react-hook-form';
import { FormInput } from '../ui/FormInput';
import { FormCheckbox } from '../ui/FormCheckbox';
import { AddressForm } from './AddressForm';
import { VALIDATION_PATTERNS, validateSiretMatchesSiren } from '../../constants/validation';

interface CompanyLocationErrors {
  siret?: { message?: string };
  address?: {
    street?: { message?: string };
    postalCode?: { message?: string };
    city?: { message?: string };
    country?: { message?: string };
    additionalInfo?: { message?: string };
  };
}

interface CompanyLocationFormProps<T extends FieldValues> {
  index: number;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  errors: CompanyLocationErrors;
  onRemove: () => void;
  siren?: string | undefined;
  canDelete?: boolean;
}

export const CompanyLocationForm = <T extends FieldValues>({
  index,
  register,
  setValue,
  getValues,
  errors,
  onRemove,
  siren,
  canDelete = true,
}: CompanyLocationFormProps<T>) => {
  const { t } = useTranslation();
  const [sameAsHeadOffice, setSameAsHeadOffice] = useState(false);

  const handleSameAsHeadOfficeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setSameAsHeadOffice(checked);
    if (!checked) return;
    const headOfficeAddress = getValues('headOfficeAddress' as Path<T>);
    setValue(`locations.${index}.address` as Path<T>, headOfficeAddress, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-md font-medium text-text">
          {t('company.location.title', { number: index + 1 })}
        </h4>
        {canDelete && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            {t('company.location.remove')}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <FormInput
          label={t('company.location.siret.label')}
          type="text"
          placeholder={t('company.location.siret.placeholder')}
          error={errors.siret?.message}
          required
          {...register(`locations.${index}.siret` as Path<T>, {
            required: t('company.location.siret.error.required'),
            setValueAs: (value) => value?.replace(/\s/g, ''),
            pattern: {
              value: VALIDATION_PATTERNS.SIRET,
              message: t('company.location.siret.error.invalid'),
            },
            validate: {
              matchesSiren: (value: string) => {
                if (!siren) return true;
                return (
                  validateSiretMatchesSiren(value, siren) ||
                  t('company.location.siret.error.mismatch_siren')
                );
              },
            },
          })}
        />

        <div className="mt-2">
          <label className="block text-sm font-medium text-text mb-2">
            {t('company.location.address.title')}
          </label>
          <div className="mb-4">
            <FormCheckbox
              label={t('company.location.same_as_head_office')}
              checked={sameAsHeadOffice}
              onChange={handleSameAsHeadOfficeChange}
            />
          </div>
          <AddressForm
            prefix={`locations.${index}.address`}
            register={register}
            errors={errors.address || {}}
            showOptionalInfo={true}
          />
        </div>
      </div>
    </div>
  );
};
